import { useContext, useState } from "react";
import { Box, Button, Group, Stack, Text } from "@mantine/core";
import { Link } from "@mantine/tiptap";
import { useEditor, EditorContent } from "@tiptap/react";
import { generateHTML } from "@tiptap/html";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import PagesEditor from "../Pages/PagesEditor";
import { AuthContext } from "../../services/context";
import {
  useGetInsightsAnalysis,
  usePatchInsightsAnalysis,
} from "../../api/insights";

const READ_ONLY_EXTENSIONS = [
  StarterKit,
  Underline,
  Link,
  Highlight,
  TextAlign.configure({ types: ["heading", "paragraph"] }),
];

const parseContent = (raw) => {
  if (!raw) return "";
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

const collectText = (node) => {
  if (!node) return "";
  if (node.text) return node.text;
  return (node.content || []).map(collectText).join("");
};

const isEmptyContent = (raw) => {
  const parsed = parseContent(raw);
  if (!parsed) return true;
  if (typeof parsed === "string") return parsed.trim() === "";
  return collectText(parsed).trim() === "";
};

const renderHtml = (raw) => {
  const parsed = parseContent(raw);
  if (!parsed) return "";
  if (typeof parsed === "string") return parsed.trim();
  return generateHTML(parsed, READ_ONLY_EXTENSIONS);
};

const PrintRichText = ({ title, stored }) => {
  const html = renderHtml(stored);
  if (!html) return null;
  return (
    <Box mb="lg" className="insights-print-only insights-analysis-block">
      {title && (
        <Text fw={600} mb="xs">
          {title}
        </Text>
      )}
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </Box>
  );
};

const InsightsAnalysisSection = ({ event, sectionKey, title }) => {
  const { user } = useContext(AuthContext);
  const isSysAdmin = user?.role === "sysAdmin";

  const eventId = event?.id;

  const { data, isPending: isPendingAnalysis } =
    useGetInsightsAnalysis(eventId);
  const { mutate: patch, isPending } = usePatchInsightsAnalysis(eventId);

  if (isPendingAnalysis) return null;

  const stored = data?.sections?.[sectionKey]?.text ?? "";
  const isAnalysisEnabled = data?.isEnabled;

  if (!isAnalysisEnabled && !isSysAdmin) return null;
  if (!isSysAdmin && isEmptyContent(stored)) return null;

  const showPrint = isAnalysisEnabled && !isEmptyContent(stored);

  return (
    <>
      <Box className="insights-print-hide">
        {isSysAdmin ? (
          <EditorView
            title={title}
            stored={stored}
            isPending={isPending}
            onSave={(value) =>
              patch({ sections: { [sectionKey]: { text: value } } })
            }
          />
        ) : (
          <ReadOnlyView title={title} stored={stored} />
        )}
      </Box>
      {showPrint && <PrintRichText title={title} stored={stored} />}
    </>
  );
};

const EditorView = ({ title, stored, onSave, isPending }) => {
  const [value, setValue] = useState(stored);
  const [dirty, setDirty] = useState(false);

  return (
    <Stack gap="xs" mb="lg">
      {title && <Text fw={600}>{title}</Text>}
      <PagesEditor
        content={parseContent(stored)}
        withImage={false}
        onChange={(json) => {
          setValue(json);
          setDirty(true);
        }}
      />
      <Group justify="flex-end">
        <Button
          size="xs"
          loading={isPending}
          disabled={!dirty}
          onClick={() => {
            onSave(value);
            setDirty(false);
          }}
        >
          Save analysis
        </Button>
      </Group>
    </Stack>
  );
};

const ReadOnlyView = ({ title, stored }) => {
  const editor = useEditor({
    extensions: READ_ONLY_EXTENSIONS,
    editable: false,
    content: parseContent(stored),
  });

  return (
    <Box mb="lg">
      {title && (
        <Text fw={600} mb="xs">
          {title}
        </Text>
      )}
      <EditorContent editor={editor} />
    </Box>
  );
};

export default InsightsAnalysisSection;
